import { z } from '@hono/zod-openapi'

// id
export const idZ = z
  .string()
  .openapi({ description: 'オーナーID', example: 'uuid-1234' })
export type id = z.infer<typeof idZ>

// name
export const nameZ = z.string().openapi({
  description: 'オーナーの氏名（姓名を結合した表示用の値）',
  example: 'Taro Yamada',
})
export type name = z.infer<typeof nameZ>

// email
export const emailZ = z.string().openapi({
  description: 'メールアドレス（ログインID）',
  example: 'taro@example.com',
})
export type email = z.infer<typeof emailZ>

// emailVerified
export const emailVerifiedZ = z
  .boolean()
  .openapi({ description: 'メールアドレス確認済みかどうか', example: false })
export type emailVerified = z.infer<typeof emailVerifiedZ>

// image
export const imageZ = z
  .string()
  .nullable()
  .openapi({ description: 'プロフィール画像URL', example: 'https://...' })
export type image = z.infer<typeof imageZ>

// twoFactorEnabled
export const twoFactorEnabledZ = z
  .boolean()
  .nullable()
  .openapi({ description: '二要素認証が有効かどうか', example: false })
export type twoFactorEnabled = z.infer<typeof twoFactorEnabledZ>

// firstName
export const firstNameZ = z
  .string()
  .openapi({ description: '名', example: 'Taro' })
export type firstName = z.infer<typeof firstNameZ>

// lastName
export const lastNameZ = z
  .string()
  .openapi({ description: '姓', example: 'Yamada' })
export type lastName = z.infer<typeof lastNameZ>

// firstNameKana
export const firstNameKanaZ = z
  .string()
  .nullable()
  .openapi({ description: '名カナ', example: 'タロウ' })
export type firstNameKana = z.infer<typeof firstNameKanaZ>

// lastNameKana
export const lastNameKanaZ = z
  .string()
  .nullable()
  .openapi({ description: '姓カナ', example: 'ヤマダ' })
export type lastNameKana = z.infer<typeof lastNameKanaZ>

// phoneNumber
export const phoneNumberZ = z
  .string()
  .openapi({ description: '電話番号（ハイフンなし）', example: '08012345678' })
export type phoneNumber = z.infer<typeof phoneNumberZ>

// emergencyPhoneNumber
export const emergencyPhoneNumberZ = z.string().nullable().openapi({
  description: '緊急用電話番号（任意・ハイフンなし）',
  example: '09012345678',
})
export type emergencyPhoneNumber = z.infer<typeof emergencyPhoneNumberZ>

// postalCode
export const postalCodeZ = z
  .string()
  .nullable()
  .openapi({ description: '郵便番号（ハイフンなし）', example: '1000001' })
export type postalCode = z.infer<typeof postalCodeZ>

// address
export const addressZ = z
  .string()
  .openapi({ description: '住所', example: '1-2-3 Chiyoda, Tokyo' })
export type address = z.infer<typeof addressZ>

// ownerAttention
export const ownerAttentionZ = z.string().nullable().openapi({
  description: 'オーナー対応時の注意事項',
  example: 'Special attention required',
})
export type ownerAttention = z.infer<typeof ownerAttentionZ>

// excludeAutoApproved
export const excludeAutoApprovedZ = z
  .string()
  .nullable()
  .openapi({ description: '自動承認の対象外とするかどうか', example: 'true' })
export type excludeAutoApproved = z.infer<typeof excludeAutoApprovedZ>

// adminMemo
export const adminMemoZ = z.string().nullable().openapi({
  description: '管理者用メモ（社内用）',
  example: 'Admin notes about this owner',
})
export type adminMemo = z.infer<typeof adminMemoZ>

// stripeCustomerId
export const stripeCustomerIdZ = z
  .string()
  .nullable()
  .openapi({ description: 'Stripe側の顧客ID', example: 'cus_1234567890' })
export type stripeCustomerId = z.infer<typeof stripeCustomerIdZ>

// createdAt
export const createdAtStringZ = z.string().openapi({
  description: '作成日時（ISO8601）',
  example: '2024-01-01T00:00:00.000Z',
})
export type createdAtString = z.infer<typeof createdAtStringZ>

export const createdAtDateZ = z
  .date()
  .openapi({ example: '2024-01-01T00:00:00.000Z' })
export type createdAtDate = z.infer<typeof createdAtDateZ>

// updatedAt
export const updatedAtStringZ = z.string().openapi({
  description: '更新日時（ISO8601）',
  example: '2024-01-01T00:00:00.000Z',
})
export type updatedAtString = z.infer<typeof updatedAtStringZ>

export const updatedAtDateZ = z
  .date()
  .openapi({ example: '2024-01-01T00:00:00.000Z' })
export type updatedAtDate = z.infer<typeof updatedAtDateZ>

// echoesOwnerId（Echoes UserinfoResponse.sub）
export const echoesOwnerIdZ = z.string().nullable().openapi({
  description:
    'Echoes 連携オーナーのオーナーID。値があれば「エコーズプラン」として扱う',
  example: '123',
})
export type echoesOwnerId = z.infer<typeof echoesOwnerIdZ>

// echoesMeta（Echoes側のメタ情報）
export const echoesMetaZ = z
  .looseObject({
    plan: z.string().optional(),
  })
  .nullable()
  .openapi({
    description: 'Echoes 側から連携されたメタ情報',
    example: { plan: 'standard' },
  })
export type echoesMeta = z.infer<typeof echoesMetaZ>

// echoesLinkedAt（Echoes連携完了日時）
export const echoesLinkedAtDateZ = z
  .date()
  .nullable()
  .openapi({ example: null })
export type echoesLinkedAtDate = z.infer<typeof echoesLinkedAtDateZ>

// echoesUpdatedAt（最後に取り込んだ Echoes 側オーナーの updated_at）
export const echoesUpdatedAtDateZ = z
  .date()
  .nullable()
  .openapi({ example: null })
export type echoesUpdatedAtDate = z.infer<typeof echoesUpdatedAtDateZ>

// echoesFailedAt（Echoes 同期に最後に失敗した日時。成功時は null）
export const echoesFailedAtDateZ = z
  .date()
  .nullable()
  .openapi({ example: null })
export type echoesFailedAtDate = z.infer<typeof echoesFailedAtDateZ>
